import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, Sparkles } from 'lucide-react';
import { formatAmount, type AiDraft } from '@muddle/core';
import { api, type AiResult } from '../api';
import { useAsync } from '../hooks/useAsync';
import { useTitle } from '../shortcuts';
import { ErrorMessage, PageHeader, Skeleton } from '../components';

export function DescribePage() {
  useTitle('Describe a drink');
  const navigate = useNavigate();
  const [status, reloadStatus] = useAsync(api.aiStatus);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AiResult | null>(null);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) {
      setError('Describe a drink, a style or how you are feeling first.');
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    try {
      setResult(await api.aiRecipes(text.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const review = (draft: AiDraft) => navigate('/drinks/new', { state: { draft } });

  if (status.status === 'error')
    return (
      <div className="page">
        <ErrorMessage message={`Couldn't load. ${status.message}`} onRetry={reloadStatus} />
      </div>
    );
  if (status.status !== 'ready')
    return (
      <div className="page">
        <Skeleton height={160} />
      </div>
    );

  return (
    <div className="page">
      <PageHeader
        kicker="Library"
        title="Describe a drink"
        sub="Name a drink, describe one, or say how you feel. Muddle searches the web, proposes recipes with their sources, and saves nothing until you approve it."
      />
      {!status.data.configured ? (
        <section className="card">
          <h2 className="card-title">Turn on AI drafting</h2>
          <p className="card-sub">
            This is optional and off until you add an Anthropic API key. Your recipes stay on this
            device; only the request you type here is sent to Anthropic.
          </p>
          <div className="actions">
            <Link className="btn btn-primary" to="/settings#ai-title">
              Add a key in Settings
            </Link>
          </div>
        </section>
      ) : (
        <form className="card" onSubmit={(e) => void search(e)} noValidate>
          <label htmlFor="ai-q">Describe a drink, or how you feel</label>
          <textarea
            id="ai-q"
            rows={2}
            maxLength={300}
            placeholder="A drink name, ingredients, a style, or a mood"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-invalid={!!error && !busy && !result}
            disabled={busy}
          />
          {error && (
            <div className="msg err" role="alert">
              {error}
            </div>
          )}
          <div className="actions">
            <button className="btn btn-primary" type="submit" disabled={busy} aria-busy={busy}>
              <Sparkles size={16} aria-hidden /> {busy ? 'Searching the web…' : 'Find recipes'}
            </button>
          </div>
          {busy && (
            <p className="card-sub" role="status">
              This usually takes 15 to 40 seconds.
            </p>
          )}
        </form>
      )}

      {result && result.drafts.length === 0 && (
        <section className="card" role="status">
          <h2 className="card-title">No usable recipe came back</h2>
          <p className="card-sub">
            Nothing was saved. Try again, or describe it differently (the drink's full name, or the
            spirit and flavours you want).
          </p>
          {result.reasons.length > 0 && (
            <details>
              <summary>Why (technical)</summary>
              <ul className="mu-sources">
                {result.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}
      {result && result.searchErrors && result.searchErrors.length > 0 && (
        <section className="card" role="status">
          <p className="card-sub">
            Web search failed ({result.searchErrors.join(', ')}), so sources could not be checked.
            If this keeps happening, make sure web search is enabled for your organization in the
            Anthropic Console.
          </p>
        </section>
      )}
      {result?.drafts.map((d, i) => (
        <DraftCard
          key={i}
          draft={d}
          have={result.matches?.[i] ?? null}
          onReview={() => review(d)}
        />
      ))}
      {result && result.drafts.length > 0 && (
        <p className="card-sub" role="status">
          About ${result.usage.estCostUsd.toFixed(3)} for this search ({result.usage.searches} web
          searches). Check amounts against the sources before relying on a draft.
        </p>
      )}
    </div>
  );
}

const KIND_LABEL = {
  established: 'Established recipe',
  adapted: 'Adapted to your request',
  original: 'New, untested',
} as const;

function DraftCard({
  draft,
  have,
  onReview,
}: {
  draft: AiDraft;
  have: { id: string; name: string } | null;
  onReview: () => void;
}) {
  return (
    <section className="card" aria-label={`Draft: ${draft.name}`}>
      <h2 className="card-title">{draft.name}</h2>
      <p>
        <span className={`pill ${draft.kind === 'established' ? 'positive' : 'neutral'}`}>
          {KIND_LABEL[draft.kind]}
        </span>
      </p>
      {have && (
        <p className="card-sub">
          You already have a drink with this name:{' '}
          <Link to={`/drinks/${have.id}`}>open {have.name}</Link>.
        </p>
      )}
      {draft.note && <p className="card-sub">{draft.note}</p>}
      {draft.adaptation && <p className="card-sub">{draft.adaptation}</p>}
      {draft.lines.map((l, i) => (
        <div key={i} className="list-row mu-draft-line">
          <span>{l.ingredient}</span>
          <span className="num">
            {formatAmount(l.amount, l.unit === 'ml' ? 1 : 2)} {l.unit}
          </span>
        </div>
      ))}
      <p className="card-sub">
        {[draft.technique, draft.glass, draft.garnish].filter(Boolean).join(' · ')}
      </p>
      {draft.sources.length ? (
        <ul className="mu-sources">
          {draft.sources.map((s) => (
            <li key={s.url}>
              <a href={s.url} target="_blank" rel="noopener noreferrer">
                {s.title} <ExternalLink size={12} aria-hidden />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
          ))}
        </ul>
      ) : draft.kind === 'original' ? (
        <p className="card-sub" role="note">
          A new creation, so there is no source. Taste it and adjust before relying on it.
        </p>
      ) : (
        <div className="msg err" role="note">
          No verifiable source for this one. Treat it with extra care.
        </div>
      )}
      <div className="actions">
        <button className="btn btn-primary" type="button" onClick={onReview}>
          Review and edit
        </button>
      </div>
    </section>
  );
}
